/**
 * SharedGeometryManager - Reference-counted geometry sharing for Three.js
 *
 * Optimizes GPU memory usage by sharing geometries across visualizer instances.
 * Instead of creating N separate SphereGeometry objects for N spheres,
 * we create one and scale individual meshes.
 *
 * Usage:
 *   const geometry = sharedGeometryManager.getSphereGeometry(1, 32, 32);
 *   // ... use geometry ...
 *   sharedGeometryManager.release(geometry);  // Dispose when refCount hits 0
 */

import * as THREE from 'three/webgpu';

/**
 * Manages shared Three.js geometries with reference counting
 */
class SharedGeometryManager {
  constructor() {
    // Map of key -> { geometry, refCount }
    this.geometries = new Map();
  }

  /**
   * Get or create a shared SphereGeometry
   * @param {number} radius - Sphere radius (use 1 for unit sphere, scale mesh instead)
   * @param {number} widthSegments - Number of horizontal segments
   * @param {number} heightSegments - Number of vertical segments
   * @returns {THREE.SphereGeometry} Shared geometry (do not dispose directly!)
   */
  getSphereGeometry(radius, widthSegments, heightSegments) {
    const key = `sphere:${radius}:${widthSegments}:${heightSegments}`;

    if (this.geometries.has(key)) {
      const entry = this.geometries.get(key);
      entry.refCount++;
      return entry.geometry;
    }

    const geometry = new THREE.SphereGeometry(radius, widthSegments, heightSegments);
    geometry.__sharedKey = key; // Tag for release lookup

    this.geometries.set(key, {
      geometry,
      refCount: 1,
      createdAt: performance.now(),
    });

    return geometry;
  }

  /**
   * Get or create a shared BoxGeometry
   * @param {number} width
   * @param {number} height
   * @param {number} depth
   * @returns {THREE.BoxGeometry}
   */
  getBoxGeometry(width, height, depth) {
    const key = `box:${width}:${height}:${depth}`;

    if (this.geometries.has(key)) {
      const entry = this.geometries.get(key);
      entry.refCount++;
      return entry.geometry;
    }

    const geometry = new THREE.BoxGeometry(width, height, depth);
    geometry.__sharedKey = key;

    this.geometries.set(key, {
      geometry,
      refCount: 1,
      createdAt: performance.now(),
    });

    return geometry;
  }

  /**
   * Get or create a shared PlaneGeometry
   * @param {number} width
   * @param {number} height
   * @param {number} widthSegments
   * @param {number} heightSegments
   * @returns {THREE.PlaneGeometry}
   */
  getPlaneGeometry(width, height, widthSegments = 1, heightSegments = 1) {
    const key = `plane:${width}:${height}:${widthSegments}:${heightSegments}`;

    if (this.geometries.has(key)) {
      const entry = this.geometries.get(key);
      entry.refCount++;
      return entry.geometry;
    }

    const geometry = new THREE.PlaneGeometry(width, height, widthSegments, heightSegments);
    geometry.__sharedKey = key;

    this.geometries.set(key, {
      geometry,
      refCount: 1,
      createdAt: performance.now(),
    });

    return geometry;
  }

  /**
   * Release a reference to a shared geometry
   * Geometry is disposed when refCount reaches 0
   * @param {THREE.BufferGeometry} geometry
   */
  release(geometry) {
    if (!geometry) return;

    const key = geometry.__sharedKey;
    if (!key) {
      // Not a shared geometry - dispose directly
      geometry.dispose?.();
      return;
    }

    const entry = this.geometries.get(key);
    if (!entry) {
      console.warn('[SharedGeometryManager] Geometry key not found:', key);
      return;
    }

    entry.refCount--;

    if (entry.refCount <= 0) {
      entry.geometry.dispose();
      this.geometries.delete(key);
    }
  }

  /**
   * Get current statistics
   * @returns {Object} Stats about shared geometries
   */
  getStats() {
    const stats = {
      totalGeometries: this.geometries.size,
      totalReferences: 0,
      geometries: [],
    };

    for (const [key, entry] of this.geometries) {
      stats.totalReferences += entry.refCount;
      stats.geometries.push({
        key,
        refCount: entry.refCount,
        age: `${Math.round(performance.now() - entry.createdAt)}ms`,
      });
    }

    return stats;
  }

  /**
   * Force dispose all geometries (call on app shutdown)
   */
  disposeAll() {
    for (const entry of this.geometries.values()) {
      entry.geometry.dispose();
    }
    this.geometries.clear();
  }

  /**
   * Debug: log current state
   */
  dump() {
    console.log('[SharedGeometryManager] Stats:', this.getStats());
  }
}

// Singleton instance for app-wide sharing
export const sharedGeometryManager = new SharedGeometryManager();
